/**
 * @file consent-banner.tsx
 * @desc GDPR consent banner for the WFW Catalyst web widget embed.
 *
 * Shown before the widget mounts when the visitor appears to be in the EU
 * (detected via Intl.DateTimeFormat().resolvedOptions().timeZone containing
 * 'Europe/'). This is a lightweight heuristic -- a proper GDPR compliance
 * system would use IP-based geo or user preference signals, but for Phase 1
 * the timezone proxy is sufficient and requires no extra API calls.
 *
 * Consent state is persisted in localStorage under key 'wfw-embed-consent':
 *   'granted'  -- user accepted; widget mounts on subsequent visits
 *   'declined' -- user declined; banner shows again on next session
 *   (missing)  -- first visit in EU; show the banner
 *
 * The component renders into the shadow DOM so the client page's CSS cannot
 * interfere with its layout or typography. All styles come from agent-widget.css.
 *
 * Props:
 *   accentColor  -- brand hex color for the Accept button (e.g. '#4f46e5')
 *   onAccept     -- called when the visitor clicks Accept; widget mounts
 *   onDecline    -- called when the visitor clicks Decline; widget does not mount
 */

import React from 'react'

/** LocalStorage key that persists the visitor's consent decision. */
export const CONSENT_STORAGE_KEY = 'wfw-embed-consent'

/** The two possible persisted consent values. */
export type ConsentValue = 'granted' | 'declined'

/**
 * Returns true when the visitor's timezone suggests they are in the EU.
 * Uses Intl.DateTimeFormat, which is universally supported in modern browsers.
 * Falls back to false on any error so non-EU visitors are never accidentally
 * shown the consent banner.
 */
export function isEuTimezone(): boolean {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone
    // All EU IANA timezones begin with 'Europe/' except Canary Islands
    // (Atlantic/Canary) and a few overseas territories. The 'Europe/' prefix
    // catches all mainland EU countries reliably.
    return tz.startsWith('Europe/')
  } catch {
    return false
  }
}

/**
 * Reads the persisted consent decision from localStorage.
 * Returns null on first visit or if localStorage is unavailable.
 */
export function readConsentFromStorage(): ConsentValue | null {
  try {
    const val = localStorage.getItem(CONSENT_STORAGE_KEY)
    if (val === 'granted' || val === 'declined') return val
    return null
  } catch {
    return null
  }
}

/**
 * Writes the consent decision to localStorage.
 * Silently ignores storage errors (quota exceeded, private browsing, etc.).
 */
export function writeConsentToStorage(value: ConsentValue): void {
  try {
    localStorage.setItem(CONSENT_STORAGE_KEY, value)
  } catch {
    // Non-critical -- widget will fall back to showing the banner again next visit
  }
}

/** Props accepted by the ConsentBanner component. */
interface ConsentBannerProps {
  /** Brand hex color used for the Accept button background. */
  accentColor: string
  /** Called when the visitor clicks Accept. */
  onAccept: () => void
  /** Called when the visitor clicks Decline. */
  onDecline: () => void
}

/**
 * GDPR consent banner component.
 *
 * Renders a small floating card bottom-right with two buttons.
 * The component is intentionally minimal -- it doesn't manage its own visibility;
 * the parent (agent.tsx) handles show/hide logic by conditionally rendering it.
 */
export function ConsentBanner({ accentColor, onAccept, onDecline }: ConsentBannerProps) {
  /** Handles the Accept click: persist decision then call onAccept. */
  const handleAccept = () => {
    writeConsentToStorage('granted')
    onAccept()
  }

  /** Handles the Decline click: remove any stored consent then call onDecline.
   *  We clear rather than write 'declined' so a browser refresh shows the banner again
   *  (giving the visitor another chance to consent on their next session). */
  const handleDecline = () => {
    try {
      localStorage.removeItem(CONSENT_STORAGE_KEY)
    } catch {
      // Ignore
    }
    onDecline()
  }

  return (
    <div className="wfw-consent-banner" role="dialog" aria-label="Privacy consent">
      <p>
        We use cookies and AI to power this chat. By continuing you agree to our terms.
      </p>
      <div className="wfw-consent-actions">
        <button
          type="button"
          className="wfw-consent-accept"
          style={{ backgroundColor: accentColor }}
          onClick={handleAccept}
          aria-label="Accept and open chat"
        >
          Accept
        </button>
        <button
          type="button"
          className="wfw-consent-decline"
          onClick={handleDecline}
          aria-label="Decline chat"
        >
          Decline
        </button>
      </div>
    </div>
  )
}
